// Where a league screen's data comes from, decided once and by identity.
//
// BACKLOG E12: real leagues running end to end, so the site can be judged by
// how it feels in use. Everything inside `/l/<id>`
// read `packages/sim`; this is the switch that lets a real league read the
// server instead.
//
// THE RULE THE STORY IS EXPLICIT ABOUT, and it is the whole design:
//
//   "A real league reads the API; a demo league reads the sim; NOTHING CHOOSES
//    AT RUNTIME BASED ON WHAT HAPPENED TO ANSWER."
//
// So this decides on **which league the URL names**, before any request is
// made, and never on whether one succeeded. A fallback would be worse than
// either source alone: a real league whose API blinked would silently show
// somebody else's practice team, with real standings one second and invented
// ones the next, and nothing on screen would say which you were looking at.
// A real league that cannot be read says so and shows nothing.
//
// WHY `useActiveLeague` IS THE ARBITER. It resolves the route's league id
// against the hub — the leagues this ACCOUNT actually has. That matters
// because the ids collide: `db:seed:local` generated the simulated world's
// fixture from the same names, so `family` is both Robin's real league and
// Ken's practice one. Membership is the thing that tells them apart, and the
// hub is the only place that knows it.
//
// THE THIRD STATE IS NOT A BUG. While the hub is still loading, the arbiter
// answers `null` — we do not yet know which kind of league this is. Guessing
// there is the failure HANDOFF calls "a route deciding before its data
// arrives", which has bitten four times. So an undecided league reads as
// loading, and the screens already have that state.

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import type { z } from 'zod';
import type { Snapshot } from '@fsp/domain';
import { useActiveLeague } from './LeaguesProvider';
import { useLeagues } from './LeaguesProvider';
import { isLoading } from '@fsp/domain';
import { useRead } from './read';
import { leagueOfPath, useMovesVersion } from './moves';
import { isDemoLeague } from './demo';
import { leagueIdFromPath } from './route';

/** Which world the league in the URL belongs to. */
export type LeagueSource = 'api' | 'sim' | 'undecided';

/**
 * Whether the league on screen is a real one.
 *
 * `undecided` while the hub is in flight. A caller must not collapse that into
 * `sim` — that is exactly the guess this file exists to refuse.
 */
export function useLeagueSource(): LeagueSource {
  const card = useActiveLeague();
  const { snapshot } = useLeagues();
  const { pathname } = useLocation();
  if (card !== null) return 'api';
  // The demo league is the API's whatever the hub says, and undecided until
  // its card arrives: a visitor's hub is the empty answer, which would read
  // as "not a real league" (O6).
  if (isDemoLeague(leagueIdFromPath(pathname))) return 'undecided';
  return isLoading(snapshot) ? 'undecided' : 'sim';
}

/**
 * One league view, from whichever world this league belongs to.
 *
 * `path` is relative to `/api` and is only ever requested for a real league —
 * pass `null` for a view whose endpoint does not exist yet and the simulated
 * value is used unconditionally, which is how the screens that have no server
 * behind them keep working while the ones that do move over.
 *
 * The simulated value arrives as a plain value because the simulator is
 * synchronous; it is wrapped in a `ready` snapshot so that every screen has
 * one shape to render and no screen has to know which world answered.
 */
export function useLeagueView<S extends z.ZodTypeAny>(
  path: string | null,
  schema: S,
  /**
   * The simulated value, or `null` when the simulator has nothing to say.
   *
   * IT MUST NOT BE COMPUTED FOR A REAL LEAGUE, and that is why this is
   * nullable rather than required. The simulator's view builders take ids and
   * look them up with a non-null assertion — `lineupView` does
   * `state.teams.find(t => t.id === teamId)!` — so handing one a REAL team id
   * does not return an empty view, it throws. Measured 2026-09-22: the lineup
   * screen was a blank page and three `Cannot read properties of undefined
   * (reading 'lineup')` in the console, because the simulated value was still
   * being computed eagerly beside the real one.
   *
   * So every caller guards its own computation on the source, and passes null
   * when it did not run.
   */
  simulated: z.output<S> | null,
  simulatedAsOf: string,
): Snapshot<z.output<S>> {
  const source = useLeagueSource();
  // Hooks cannot be conditional, so the read is always declared and is handed
  // `null` — which `useRead` treats as "do not ask" — whenever this is not a
  // real league. That keeps the simulator from generating traffic.
  const ask = source === 'api' && path !== null ? path : null;
  // A MOVE IN THIS LEAGUE IS READ BACK EVERYWHERE IT SHOWS (W8): the pool, the
  // claims, the roster in the lineup, the card. Every league read asks again
  // when this tab makes one, over what it already holds.
  const refresh = useMovesVersion(leagueOfPath(ask));
  const { snapshot } = useRead(ask, schema, { refresh });

  return useMemo(
    () => viewSnapshot(source, path, snapshot as Snapshot<z.output<S>>, simulated, simulatedAsOf),
    [source, path, snapshot, simulated, simulatedAsOf],
  );
}

/**
 * The rule, out of the hook so it can be driven as calls (the `reader.ts`
 * pattern, AUDIT-2026-09-19 F23).
 *
 * A REAL LEAGUE WITH NO PATH YET IS LOADING, NOT A DEMO LEAGUE. The first
 * version fell through to the simulator branch when `source === 'api'` and
 * the caller had no id to build a path from — which is every real league's
 * Lineup and Matchup while Home is in flight, and Matchup for good when there
 * is no game — and drew "The practice league has nothing to show here." over
 * a real league (AUDIT-2026-09-23 U1). That sentence is now reachable only
 * when the league IS the practice one.
 */
export function viewSnapshot<T>(
  source: LeagueSource,
  path: string | null,
  snapshot: Snapshot<T>,
  simulated: T | null,
  simulatedAsOf: string,
): Snapshot<T> {
  if (source === 'undecided') return { state: 'loading' };
  if (source === 'api') return path !== null ? snapshot : { state: 'loading' };
  if (simulated === null) {
    // A demo league the simulator cannot answer for. Says so rather than
    // drawing an empty screen that reads as "there is nothing here".
    return {
      state: 'failed',
      failure: { code: 'unavailable', message: 'The practice league has nothing to show here.' },
      lastValue: null,
      lastAsOf: null,
    };
  }
  return { state: 'ready', value: simulated, asOf: simulatedAsOf, stale: false };
}
